import { Spacing } from '@persona-id/design-tokens';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import IntegrationHeroCard from 'dashboard/components/IntegrationHeroCard';
import IntegrationStep from 'dashboard/components/IntegrationStep';
import TemplateSelector from 'dashboard/components/TemplateSelector/TemplateSelector';
import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import HostedFlowCodeSnippet from 'dashboard/views/DashboardGettingStartedHostedFlowView/HostedFlowCodeSnippet';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import HostedFlowImage from 'images/dashboard/getting-started/hosted-flow.svg';
import CreateInquiryDemo from 'images/dashboard/getting-started/hosted-flow-create-inquiry-demo.gif';

const CreateInquiryDemoGif = styled('img')`
  width: 100%;
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
`;

const ListItem = styled('li')`
  margin: ${Spacing[2]} 0px;
  font-size: ${({ theme }) => theme.text.paragraph.md.fontSize};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

interface DashboardGettingStartedHostedViewProps {
  currentEnvironment: Environment;
  organization: Organization;
  selectedTemplate: Template | InquiryTemplate | undefined;
  templates: Array<Template | InquiryTemplate>;
  handleTemplateSelect: (templateId: string) => void;
}

export default function DashboardGettingStartedHostedFlowView({
  currentEnvironment,
  handleTemplateSelect,
  organization,
  selectedTemplate,
  templates,
}: DashboardGettingStartedHostedViewProps) {
  return (
    <>
      <IntegrationHeroCard title='Hosted Flow' image={HostedFlowImage}>
        With the hosted flow integration method, you can create an information collection experience
        (Inquiry) and share a link to it with your customer. It requires no coding, and each
        experience is spawned from an Inquiry template, which dictates what type of information is
        collected and which Verifications you want to run during the experience.
      </IntegrationHeroCard>
      <Card>
        <Card.Body>
          <Card.Title>How to use this method</Card.Title>
          <IntegrationStep title='Create and send your Inquiry' stepNumber={1}>
            <Paragraph color='primary'>
              In your Dashboard, open the{' '}
              <Link to='/dashboard/inquiries' target='_blank'>
                Inquiries
              </Link>{' '}
              page and click Create Inquiry. From here, you can generate a hosted flow link to send
              to individuals to verify their identity. Here’s what you can expect to see when you’re
              creating your Inquiry:
            </Paragraph>
            <Spacer height={2} />
            <CreateInquiryDemoGif src={CreateInquiryDemo} />
            <Spacer height={2} />
            <Paragraph color='primary'>
              We recommend inputting a Reference ID when you’re creating your Inquiry. The reference
              ID represents a unique individual in your business’ database, like a User ID. You can
              then use the reference ID to search for that specific individual in your Dashboard.
            </Paragraph>
            <Divider full />
            <Paragraph color='primary'>
              Alternatively, if you do not want to create your hosted flow links manually, you can
              use the link below wherever you’d like your customers to go through the flow and it
              will automatically generate a new Inquiry each time. Make sure to select the Inquiry
              Template you’d like to use first!
            </Paragraph>
            <Spacer height={2} />
            {templates.length >= 1 && selectedTemplate ? (
              <TemplateSelector
                handleTemplateSelect={handleTemplateSelect}
                templates={templates}
                value={selectedTemplate.id}
                label='Inquiry Template'
                size='sm'
              />
            ) : (
              <Paragraph>Loading... try refreshing the page!</Paragraph>
            )}
            <Spacer height={2} />
            {selectedTemplate && (
              <HostedFlowCodeSnippet
                id='getting-started__hosted-flow-link'
                template={selectedTemplate}
                environment={currentEnvironment}
                organization={organization}
              />
            )}
          </IntegrationStep>
          <Divider full />
          <IntegrationStep title='Optimize' stepNumber={2}>
            <Paragraph color='primary'>
              That’s it — congrats! Now you can test the experience and see if you would like to do
              any additional optimizations. Some common optimizations to consider include:
            </Paragraph>
            <UnorderedList>
              <ListItem>
                Using our{' '}
                <a
                  href='https://docs.withpersona.com/reference/introduction'
                  target='_blank'
                  rel='noreferrer'
                >
                  API
                </a>{' '}
                to create hosted flow links and then using these links to have customers resume an
                Inquiry
              </ListItem>
              <ListItem>
                Using the <code>redirect-uri</code> parameter to redirect to a page when the user
                finishes the experience
              </ListItem>
              <ListItem>
                Using the <code>fields</code> parameter to dynamically present Inquiry field values
                such as name or birthday, so that your users simply need to confirm that those
                values are correct
              </ListItem>
              <ListItem>
                Setting a{' '}
                <Link to='/dashboard/inquiry-manage-domain'>
                  custom subdomain for your hosted flow links
                </Link>
              </ListItem>
              <ListItem>... and more!</ListItem>
            </UnorderedList>
            <Paragraph color='primary'>
              Learn more about these optimizations{' '}
              <a
                href='https://docs.withpersona.com/docs/hosted-flow-fields'
                target='_blank'
                rel='noreferrer'
              >
                here
              </a>
              .
            </Paragraph>
          </IntegrationStep>
        </Card.Body>
      </Card>
    </>
  );
}
